import React from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Card, CardBody, Badge } from "reactstrap";
import { useQuery } from "react-query";
import { getUserSubscriptionById } from "helpers/fakebackend_helper";
import { IMAGE_BASE_URL } from "constants/imageUrl";

const UserDetailsModal = ({ isOpen, toggle, userData, isLoading }) => {
  // Query for user subscription status
  const { data: subscriptionData, isLoading: isSubscriptionLoading } = useQuery(
    ['user_subscription_modal', userData?.user?.id],
    () => getUserSubscriptionById(userData?.user?.id),
    {
      enabled: !!userData?.user?.id && isOpen,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <h4 className="modal-title">User Details</h4>
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading user details...</p>
          </div>
        ) : userData?.user ? (
          <div>
            {/* Basic Information */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="card-title mb-3">Basic Information</h5>
                <Row>
                  <Col md={6}>
                    <p><strong>ID:</strong> {userData.user.id}</p>
                    <p><strong>Name:</strong> {userData.user.name}</p>
                    <p><strong>Email:</strong> {userData.user.email}</p>
                    <p><strong>Role:</strong> 
                      <Badge color="primary" className="ml-2">
                        {userData.user.role}
                      </Badge>
                    </p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Space Role:</strong> 
                      <Badge color="info" className="ml-2">
                        {userData.user.space_role}
                      </Badge>
                    </p>
                    <p><strong>Created At:</strong> {new Date(userData.user.created_at).toLocaleString()}</p>
                    <p><strong>Updated At:</strong> {new Date(userData.user.updated_at).toLocaleString()}</p>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Profile Information */}
            {userData.user.profile && (
              <Card className="mb-3">
                <CardBody>
                  <h5 className="card-title mb-3">Profile Information</h5>
                  <Row>
                    <Col md={6}>
                      <p><strong>Age:</strong> {userData.user.profile.age}</p>
                      <p><strong>Date of Birth:</strong> {userData.user.profile.date_of_birth}</p>
                      <p><strong>Number of Kids:</strong> {userData.user.profile.number_of_kids}</p>
                      <p><strong>Contact Details:</strong> {userData.user.profile.contact_details}</p>
                      <p><strong>Emergency Contact:</strong> {userData.user.profile.emergency_contact}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Partner&apos;s Name:</strong> {userData.user.profile.partners_name}</p>
                      <p><strong>Parent Role:</strong> 
                        <Badge color="success" className="ml-2">
                          {userData.user.profile.parent_role}
                        </Badge>
                      </p>
                      <p><strong>Address:</strong> {userData.user.profile.address}</p>
                      <p><strong>Passport ID:</strong> 
                        {userData.user.profile.passport_url ? (
                          <a 
                            href={`${IMAGE_BASE_URL}/${userData.user.profile.passport_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            download
                            className="ml-2"
                          >
                            <i className="bx bx-download"></i> Download Passport ID
                          </a>
                        ) : (
                          <span className="text-muted ml-2">Not provided</span>
                        )}
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Subscription Information */}
            <Card className="mb-3">
              <CardBody>
                <h5 className="card-title mb-3">Subscription Information</h5>
                {isSubscriptionLoading ? (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading subscription details...</p>
                  </div>
                ) : subscriptionData?.has_subscription ? (
                  <Row>
                    <Col md={6}>
                      <p><strong>Status:</strong> 
                        <Badge color={subscriptionData.is_trial ? "warning" : "success"} className="ml-2">
                          {subscriptionData.is_trial ? "Trial" : subscriptionData.subscription?.status}
                        </Badge>
                      </p>
                      <p><strong>Stripe Subscription ID:</strong> {subscriptionData.subscription?.stripe_subscription_id}</p>
                      <p><strong>Stripe Customer ID:</strong> {subscriptionData.subscription?.stripe_customer_id}</p>
                      <p><strong>Price ID:</strong> {subscriptionData.subscription?.stripe_price_id}</p>
                    </Col>
                    <Col md={6}>
                      {subscriptionData.is_trial && subscriptionData.subscription?.trial_ends_at && (
                        <p><strong>Trial Ends:</strong> {new Date(subscriptionData.subscription.trial_ends_at).toLocaleString()}</p>
                      )}
                      {subscriptionData.subscription?.current_period_start && (
                        <p><strong>Current Period Start:</strong> {new Date(subscriptionData.subscription.current_period_start).toLocaleString()}</p>
                      )}
                      {subscriptionData.subscription?.current_period_end && (
                        <p><strong>Current Period End:</strong> {new Date(subscriptionData.subscription.current_period_end).toLocaleString()}</p>
                      )}
                      {subscriptionData.subscription?.canceled_at && (
                        <p><strong>Canceled At:</strong> {new Date(subscriptionData.subscription.canceled_at).toLocaleString()}</p>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center">
                    <Badge color="danger" className="mb-2">No Active Subscription</Badge>
                    <p className="text-muted">This user does not have an active subscription.</p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Active Parenting Space */}
            {userData.user.active_parenting_space && (
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">Active Parenting Space</h5>
                  <Row>
                    <Col md={6}>
                      <p><strong>Space ID:</strong> {userData.user.active_parenting_space.id}</p>
                      <p><strong>Space Name:</strong> {userData.user.active_parenting_space.name}</p>
                      <p><strong>Created By:</strong> {userData.user.active_parenting_space.created_by}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Currently With Parent ID:</strong> {userData.user.active_parenting_space.currently_with_parent_id}</p>
                      <p><strong>Last Updated:</strong> {new Date(userData.user.active_parenting_space.currently_with_parent_last_updated).toLocaleString()}</p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center">
            <p>No user data available</p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

UserDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      email: PropTypes.string,
      role: PropTypes.string,
      space_role: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
      profile: PropTypes.shape({
        age: PropTypes.number,
        date_of_birth: PropTypes.string,
        number_of_kids: PropTypes.number,
        contact_details: PropTypes.string,
        emergency_contact: PropTypes.string,
        partners_name: PropTypes.string,
        parent_role: PropTypes.string,
        address: PropTypes.string,
        passport_url: PropTypes.string,
      }),
      active_parenting_space: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        created_by: PropTypes.number,
        currently_with_parent_id: PropTypes.number,
        currently_with_parent_last_updated: PropTypes.string,
      }),
    }),
  }),
  isLoading: PropTypes.bool.isRequired,
};

export default UserDetailsModal;
